import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Droplets, Zap, Leaf, ArrowRight, FileBarChart2 } from "lucide-react";
import Topbar from "Components/Topbar/Topbar";
import ReportConfigPage from "./ReportConfigPage";
import ReportPreviewPage from "./ReportPreviewPage";
import type { User as BackendUser } from "../../types/user";
import "./ReportsPage.css";

interface ReportsPageProps {
  user: BackendUser;
}

export type ReportModule = "eau" | "energie" | "carbone";

export interface ReportConfig {
  yearN: number;
  yearN1: number;
  startMonth: number;
  endMonth: number;
  filters: {
    bus: (number | string)[];
    activites: (number | string)[];
    filieres: (number | string)[];
    regions: (number | string)[];
    societes: (number | string)[];
    sites: (number | string)[];
  };
}

type PageView =
  | { view: "selection" }
  | { view: "config"; module: ReportModule }
  | { view: "preview"; module: ReportModule; config: ReportConfig };

interface ModuleCard {
  id: ReportModule;
  title: string;
  description: string;
  icon: React.ReactNode;
  accentColor: string;
  bgGradient: string;
  iconBg: string;
}

const MODULES: ModuleCard[] = [
  {
    id: "eau",
    title: "Rapport Eau",
    description: "Analyse comparative de la consommation d'eau par période, site et filière.",
    icon: <Droplets size={28} />,
    accentColor: "#3b82f6",
    bgGradient: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    iconBg: "#3b82f6",
  },
  {
    id: "energie",
    title: "Rapport Énergie",
    description: "Suivi et comparaison des consommations énergétiques par type et unité.",
    icon: <Zap size={28} />,
    accentColor: "#f59e0b",
    bgGradient: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
    iconBg: "#f59e0b",
  },
  {
    id: "carbone",
    title: "Bilan Carbone",
    description: "Empreinte carbone et émissions GES par scope, activité et campagne.",
    icon: <Leaf size={28} />,
    accentColor: "#059669",
    bgGradient: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
    iconBg: "#059669",
  },
];

const ReportsPage: React.FC<ReportsPageProps> = ({ user }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState<PageView>({ view: "selection" });

  if (page.view === "config") {
    return (
      <ReportConfigPage
        user={user}
        module={page.module}
        onBack={() => setPage({ view: "selection" })}
        onPreview={(config) => setPage({ view: "preview", module: page.module, config })}
      />
    );
  }

  if (page.view === "preview") {
    return (
      <ReportPreviewPage
        user={user}
        module={page.module}
        config={page.config}
        onBack={() => setPage({ view: "config", module: page.module })}
      />
    );
  }

  return (
    <div className="reports-page">
      <Topbar
        title="Rapport d'indicateurs"
        userName={user.username}
        onLogout={() => navigate("/")}
        user={user}
      />

      <div className="reports-content">
        {/* Header */}
        <div className="reports-header">
          <div className="reports-header-left">
            <div className="reports-icon-wrapper" style={{ background: "#d97706" }}>
              <FileBarChart2 size={24} color="white" />
            </div>
            <div>
              <h1 className="reports-title">Rapport d'indicateurs</h1>
              <p className="reports-subtitle">
                Générez des rapports comparatifs pour analyser vos performances environnementales
              </p>
            </div>
          </div>
        </div>

        {/* Module selection */}
        <div className="reports-section-label">Sélectionnez un module</div>

        <div className="reports-modules-grid">
          {MODULES.map((mod) => (
            <button
              key={mod.id}
              className="reports-module-card"
              style={{ "--card-accent": mod.accentColor } as React.CSSProperties}
              onClick={() => setPage({ view: "config", module: mod.id })}
              aria-label={`Ouvrir ${mod.title}`}
            >
              <div className="reports-module-card-inner" style={{ background: mod.bgGradient }}>
                <div className="reports-module-icon" style={{ background: mod.iconBg }}>
                  {mod.icon}
                </div>
                <div className="reports-module-body">
                  <h2 className="reports-module-title">{mod.title}</h2>
                  <p className="reports-module-desc">{mod.description}</p>
                </div>
                <div className="reports-module-arrow" style={{ color: mod.accentColor }}>
                  <ArrowRight size={20} />
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Info banner */}
        <div className="reports-info-banner">
          <div className="reports-info-icon">
            <FileBarChart2 size={18} color="#d97706" />
          </div>
          <p className="reports-info-text">
            Chaque rapport vous permet de comparer deux périodes (N et N-1), d'appliquer des filtres
            organisationnels et de visualiser les évolutions de vos indicateurs clés.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
